"use client"

import { IUser } from "@/components/composition-flow/composition.new.types"
import { Composition } from "@/components/snippet/stores/memory-store/optimized-composition-structure"
import type { UserProfile } from "@/lib/user-profile-cache"
import { create } from "zustand"
import { immer } from "zustand/middleware/immer"
import { enableMapSet } from "immer"
import { createJSONStorage, persist } from "zustand/middleware"
import { createClient } from "@/utils/supabase/client"

// * ? ATTENTION: React 19 - Review for compatibility
// Enable Immer support for ES6 Map/Set mutations used in this store
enableMapSet()

const toProfileMap = (obj: any): Map<string, UserProfile> => (obj instanceof Map ? obj : new Map(Object.entries(obj ?? {})))
const PROFILE_TTL_MS = 5 * 60 * 1000

type UserInfoType = {
    auth_id: string,
    avatarImageUrl: string,
    username: string,
    firstName: string,
    emailAdress: string,
    languagePreference: 'zh-CN' | 'en' | 'zh-HK',
    firstComposition: Composition | Partial<Composition> | undefined,
    accessToken: string | undefined,
    refreshToken: string | undefined,
}

export interface UserStore {
    user: IUser | undefined,
    userInfo: UserInfoType | undefined,
    updateUserInfo: (newUserInfo: Partial<UserInfoType>) => void,
    updateUser: (newUser: Partial<IUser>) => void,
    // Profile cache management
    getUserProfileFromSupabase: () => Promise<IUser | Partial<IUser> | null>,
    profileCache: Map<string, UserProfile>,
    getUserProfile: (userId: string, force?: boolean) => Promise<UserProfile | null>,
    updateCachedProfile: (userId: string, updates: Partial<UserProfile>) => void,
    invalidateProfile: (userId: string) => Promise<void>,
    preloadProfiles: (userIds: string[]) => Promise<void>,
}

export const useUserStore = create<UserStore>()(
    persist(
        immer((set, get) => {
            return {
                user: undefined,
                userInfo: {
                    auth_id: "",
                    avatarImageUrl: "",
                    username: "",
                    firstName: "",
                    emailAdress: "",
                    languagePreference: 'en',
                    firstComposition: undefined,
                    accessToken: undefined,
                    refreshToken: undefined,

                },
                profileCache: new Map<string, UserProfile>(),

                updateUserInfo: (newUserInfo: Partial<UserInfoType>) => set((state) => {
                    if (!state.userInfo) {
                        state.userInfo = newUserInfo as UserInfoType;
                        return;
                    }
                    state.userInfo = {
                        ...state.userInfo,
                        ...newUserInfo
                    };
                }),

                getUserProfileFromSupabase: async () => {
                    const supabase = createClient()
                    const userSession = await supabase.auth.getSession()

                    if (userSession.error || !userSession.data.session || !userSession.data.session.user) {
                        console.error(userSession)
                        return null
                    }

                    const userId = userSession.data.session.user.id

                    const { data: userProfile, error: userProfileError } = await supabase.from('profiles').select('*').eq('id', userId).single()



                    if (userProfileError) {
                        console.error("current user error", userProfileError)
                        return null
                    }
                    return userProfile as IUser

                },

                updateUser: (newUser: Partial<IUser>) => set((state) => {
                    if (!state.user) {
                        state.user = newUser as IUser;
                        return;
                    }
                    state.user = {
                        ...state.user,
                        ...newUser
                    };
                }),

                // Profile cache methods
                getUserProfile: async (userId: string, force?: boolean): Promise<UserProfile | null> => {
                    let state = get();
                    if (!(state.profileCache instanceof Map)) {
                        set((s) => {
                            s.profileCache = toProfileMap(s.profileCache)
                        })
                        state = get()
                    }

                    // If force is true, invalidate caches before fetching
                    if (force) {
                        const cache = toProfileMap(state.profileCache)
                        cache.delete(userId)
                        set((s) => { s.profileCache = cache })
                    } else {
                        // Check local cache first
                        const cached = state.profileCache.get(userId);
                        if (cached) {
                            const fresh = Date.now() - cached.cached_at < PROFILE_TTL_MS
                            if (fresh) {
                                return cached
                            }
                        }
                    }

                    const supabase = createClient()
                    const { data: profileRow, error } = await supabase
                        .from('profiles')
                        .select('*')
                        .eq('id', userId)
                        .single()
                    if (error) {
                        return null
                    }
                    const profile = { ...profileRow, cached_at: Date.now() } as UserProfile

                    if (profile) {
                        // Update local cache
                        set((state) => {
                            state.profileCache = toProfileMap(state.profileCache)
                            state.profileCache.set(userId, profile);
                        });
                    }

                    return profile;
                },

                updateCachedProfile: (userId: string, updates: Partial<UserProfile>) => {
                    set((state) => {
                        const cache = toProfileMap(state.profileCache)
                        const existing = cache.get(userId);
                        if (existing) {
                            const updated = { ...existing, ...updates, cached_at: Date.now() };
                            cache.set(userId, updated);
                        }
                        state.profileCache = cache
                    });


                },

                invalidateProfile: async (userId: string) => {
                    set((state) => {
                        const cache = toProfileMap(state.profileCache)
                        cache.delete(userId)
                        state.profileCache = cache
                    });

                },

                preloadProfiles: async (userIds: string[]) => {
                    let state = get();
                    if (!(state.profileCache instanceof Map)) {
                        set((s) => {
                            s.profileCache = toProfileMap(s.profileCache)
                        })
                        state = get()
                    }
                    const uncachedIds = userIds.filter(id => !state.profileCache.has(id));

                    if (uncachedIds.length > 0) {
                        const supabase = createClient()
                        const { data: rows, error } = await supabase
                            .from('profiles')
                            .select('*')
                            .in('id', uncachedIds)
                        if (error) {
                            return
                        }
                        set((state) => {
                            const cache = toProfileMap(state.profileCache);
                            (rows ?? []).forEach((profile: any, index: number) => {
                                if (profile) {
                                    cache.set(uncachedIds[index], { ...profile, cached_at: Date.now() } as UserProfile)
                                }
                            })
                            state.profileCache = cache
                        })
                    }
                },
            }
        }),
        {
            name: 'userstore-some-id',
            storage: createJSONStorage(() => {
                if (typeof window !== 'undefined') {
                    return localStorage
                }
                return {
                    getItem: () => null,
                    setItem: () => { },
                    removeItem: () => { },
                }
            }),
            merge: (persistedState, currentState) => {
                const ps = persistedState as any
                const cs = currentState as any
                return {
                    ...cs,
                    ...ps,
                    profileCache: toProfileMap(ps?.profileCache ?? cs?.profileCache),
                }
            },
        }
    )
)
